/**
 * Money primitives. Ported from quote-gen `catalog.to_cents`.
 *
 * Every amount in the engine is an INTEGER number of cents so a total can never drift
 * by a penny across a dozen multiplications. Dollars appear only at the two boundaries:
 * reading the catalog (`toCents`) and rendering (`formatMoney`).
 */

/**
 * Dollars → integer cents, half-up.
 *
 * The Python original uses `Decimal(str(amount)) * 100` precisely because
 * `89.99 * 100 === 8998.999999999999` in binary floating point — a naive `Math.round`
 * happens to save that case but not all of them. Scaling the decimal STRING sidesteps
 * the representation error entirely rather than rounding around it.
 */
export function toCents(amount: number | string | null | undefined): number | null {
  if (amount === null || amount === undefined || amount === '') return null;
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return null;

  const negative = n < 0;
  const [whole, frac = ''] = Math.abs(n).toFixed(10).split('.');
  // Two decimal places of cents, then half-up on the third.
  const centsStr = `${whole}${frac.slice(0, 2).padEnd(2, '0')}`;
  let cents = Number(centsStr);
  if (Number(frac.charAt(2) || '0') >= 5) cents += 1;
  return negative ? -cents : cents;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: '$',
  eur: '€',
  gbp: '£',
  cad: 'CA$',
  aud: 'A$',
};

export function currencySymbol(currency: string | undefined): string {
  return CURRENCY_SYMBOLS[(currency || 'usd').toLowerCase()] ?? '$';
}

/** Integer cents → display string. `null` renders as an em dash, matching render.py. */
export function formatMoney(cents: number | null | undefined, currency: string): string {
  if (cents === null || cents === undefined) return '—';
  const sym = currencySymbol(currency);
  const value = (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sym}${value}`;
}
